<script setup lang="ts">
import { computed, shallowRef } from 'vue';
import { store } from '@/app/store';
import { useLegacyCarouselPager } from '@/components/legacy-gallery/useLegacyCarouselPager';
import {
  LEGACY_GALLERY_EMPTY_SELECTION,
  useLegacyGalleryFacade,
} from '@/components/legacy-gallery/useLegacyGalleryFacade';
import { useLegacyGalleryTelemetry } from '@/components/legacy-gallery/useLegacyGalleryTelemetry';
import type { ArtStyleClickEvent, ArtStyleGalleryItem } from './artStyleTypes';

const props = defineProps<{
  items: readonly ArtStyleGalleryItem[];
  itemsPerSlide: number;
  locked: boolean;
}>();

const emit = defineEmits<{
  select: [event: ArtStyleClickEvent, item: ArtStyleGalleryItem];
}>();

const imageElements = shallowRef<Array<HTMLImageElement | null>>([]);
const actualItemsPerSlide = computed(() =>
  store.viewportIsPortrait || store.isVerticalKiosk ? 1 : props.itemsPerSlide
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const isCentered = computed(() => props.items.length < props.itemsPerSlide);

const telemetry = useLegacyGalleryTelemetry({
  actualItemsPerSlide,
  isGridMode: false,
  items: () => props.items,
  selectedItemUuids: LEGACY_GALLERY_EMPTY_SELECTION,
  selectionMode: null,
});
const pager = useLegacyCarouselPager({
  actualItemsPerSlide,
  isGridMode: false,
  items: () => props.items,
  onSwitchSlide: (
    delta: number,
    firstVisibleItemIndex: number,
    visibleItems: Array<ArtStyleGalleryItem | undefined>
  ) => {
    telemetry.recordSlideSwitch({
      delta,
      firstVisibleItemIndex,
      visibleItems,
    });
  },
});

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onItemClick(event: MouseEvent, item: ArtStyleGalleryItem): void {
  if (telemetry.recordItemClick(event, item)) {
    emit('select', event, item);
  }
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onImageError(
  item: ArtStyleGalleryItem,
  index: number,
  event: Event
): void {
  telemetry.recordImageLoadError(item, index, event);
}

const {
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setContainerElement,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setImageElement,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setItemElement,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setScrollableElement,
} = useLegacyGalleryFacade({
  actualItemsPerSlide,
  imageElements,
  items: () => props.items,
  pager,
});
</script>

<template>
  <div
    :ref="setContainerElement"
    class="ImageGallery"
    :class="{
      'ImageGallery--arrow-prev-hidden': pager.hidePrevArrow.value,
      'ImageGallery--arrow-next-hidden': pager.hideNextArrow.value,
      ArtStyle__gallery: true,
      'pointer-events-none': locked,
    }"
  >
    <button
      class="ImageGallery__arrow ImageGallery__arrow--prev"
      @click="pager.switchSlide(-1)"
    />
    <button
      class="ImageGallery__arrow ImageGallery__arrow--next"
      @click="pager.switchSlide(1)"
    />

    <div
      :ref="setScrollableElement"
      class="ImageGallery__scrollable hidden-scrollbar"
      :class="{ 'ImageGallery__scrollable--centered': isCentered }"
    >
      <ul class="ImageGallery__list">
        <li
          v-for="(item, index) in items"
          :key="pager.getItemRenderKey(item, index)"
          :ref="(element) => setItemElement(element, index)"
          class="ImageGallery__item ArtStyle__gallery-item"
          :class="{
            'ArtStyle__gallery-item--selected': item.selected,
          }"
          data-testid="gallery-item"
          data-selected="false"
          data-loading="false"
          data-error="false"
          @click="onItemClick($event, item)"
        >
          <div
            class="ImageGallery__item-wrapper ArtStyle__gallery-wrapper"
            :style="
              item.aspectRatio
                ? { '--ImageGallery--item-ratio': item.aspectRatio }
                : undefined
            "
          >
            <div
              class="ImageGallery__item-overlay ArtStyle__gallery-overlay"
            >
              <h3
                class="ArtStyle__gallery-item-title"
                :class="{
                  'ArtStyle__gallery-item-title--selected': item.selected,
                }"
              >
                {{ item.title }}
              </h3>
            </div>

            <img
              :ref="(element) => setImageElement(element, index)"
              class="ImageGallery__item-image ArtStyle__gallery-image"
              :src="item.url"
              @error="onImageError(item, index, $event)"
            />

            <i
              v-show="false"
              class="ImageGallery__item-checkmark kiosk-success-icon"
            />
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .ArtStyle__gallery {
    --ImageGallery--item-width: var(--legacy-gallery-item-width, 26rem);
    --ImageGallery--item-gap: var(--legacy-gallery-item-gap, 2.7rem);
    --ImageGallery--item-ratio: 1;

    padding: 0;
    margin: 0 auto 5rem;
    max-width: 100%;

    .ImageGallery__scrollable {
      // Reserve room outside the scrollport for the scaled hover image.
      padding-block: 1.5rem;
      margin-block: -1.5rem;
    }
  }

  .ArtStyle__gallery-wrapper {
    display: flex;
    flex-direction: column;
    height: calc(100% + 4rem);
  }

  // No :hover zoom here on purpose. The kiosk is a touchscreen, and touch
  // browsers keep :hover on the last touched element until another one is
  // touched, so the effect stuck: swiping the carousel left a card 10% larger
  // than its neighbours with no way back, and a tap left it that way for the
  // 1s before navigation.
  .ArtStyle__gallery-item {
    cursor: pointer;
    display: flex;
    flex-direction: column;
    overflow: visible;
    margin-bottom: 2rem;
  }

  .ArtStyle__gallery-image {
    position: relative;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .ArtStyle__gallery-overlay {
    pointer-events: none;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .ArtStyle__gallery-item-title {
    position: absolute;
    bottom: -3.5rem;
    left: 0;
    right: 0;
    text-align: center;
    padding: 0.5rem;
    color: var(--Kiosk--font_color);
    font-size: 1.21rem;
    letter-spacing: 1px;
    text-transform: var(--Kiosk--font_capitalize, uppercase);
    pointer-events: auto;
    z-index: 10;
    white-space: nowrap;

    &--selected {
      color: #6496FF;
    }
  }
</style>
